import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Users, ShoppingBag, IndianRupee, Clock, Loader, CheckCircle2, XCircle,
  Wallet, CalendarDays, TrendingUp, ScrollText, FileDown,
} from "lucide-react";
import aapi, { inr, fmtDate } from "./aapi";
import { StatCard, PageHeader } from "@/panel/components";
import { Counter, EASE } from "@/components/Motion";

const tooltipStyle = {
  background: "rgb(var(--panel))",
  border: "1px solid rgb(var(--gold) / 0.25)",
  borderRadius: 12,
  fontSize: 12,
  color: "#F7F7F8",
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [revBusy, setRevBusy] = useState(false);

  useEffect(() => {
    aapi.get("/overview").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const downloadRevenuePdf = async () => {
    setRevBusy(true);
    try {
      const res = await aapi.get("/reports/revenue.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers?.["content-disposition"] || "";
      const match = dispo.match(/filename=([^;]+)/);
      a.download = match ? match[1].trim() : "beatkingsmm-revenue.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setRevBusy(false);
    }
  };

  if (!data)
    return (
      <div className="flex justify-center py-32">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" data-testid="admin-dashboard-loading" />
      </div>
    );

  const s = data.by_status;

  return (
    <div data-testid="admin-dashboard-page">
      <PageHeader
        title={
          <>
            Platform <span className="font-serif italic text-gold-grad">command.</span>
          </>
        }
        sub="The entire BEATKINGSMM operation, one screen."
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <StatCard icon={Users} label="Total Users" testid="admin-stat-users" delay={0}>
          <Counter to={data.total_users} duration={1.4} />
        </StatCard>
        <StatCard icon={ShoppingBag} label="Total Orders" testid="admin-stat-orders" delay={0.05}>
          <Counter to={data.total_orders} duration={1.4} />
        </StatCard>
        <StatCard icon={IndianRupee} label="Total Revenue" testid="admin-stat-revenue" delay={0.1} accent>
          <Counter to={data.total_revenue} decimals={2} prefix="₹" duration={1.6} />
        </StatCard>
        <StatCard icon={Wallet} label="Balance Added" testid="admin-stat-added" delay={0.15}>
          <Counter to={data.total_added} decimals={2} prefix="₹" duration={1.6} />
        </StatCard>
        <StatCard icon={CalendarDays} label="Today's Orders" testid="admin-stat-today-orders" delay={0.2}>
          <Counter to={data.today_orders} duration={1.4} />
        </StatCard>
        <StatCard icon={TrendingUp} label="Today's Revenue" testid="admin-stat-today-revenue" delay={0.25}>
          <Counter to={data.today_revenue} decimals={2} prefix="₹" duration={1.6} />
        </StatCard>
        <StatCard icon={Clock} label="Pending" testid="admin-stat-pending" delay={0.3}>
          <Counter to={s.pending || 0} duration={1.2} />
        </StatCard>
        <StatCard icon={Loader} label="In Progress" testid="admin-stat-in-progress" delay={0.35}>
          <Counter to={s.in_progress || 0} duration={1.2} />
        </StatCard>
        <StatCard icon={CheckCircle2} label="Completed" testid="admin-stat-completed" delay={0.4}>
          <Counter to={s.completed || 0} duration={1.2} />
        </StatCard>
        <StatCard icon={XCircle} label="Cancelled" testid="admin-stat-cancelled" delay={0.45}>
          <Counter to={s.cancelled || 0} duration={1.2} />
        </StatCard>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
        className="relative mt-6 overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-br from-gold/[0.08] via-panel to-panel p-6 sm:p-8"
        data-testid="admin-platform-card"
      >
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-4xl font-bold tracking-tight text-neutral-50 sm:text-6xl" data-testid="admin-platform-total">
              <Counter to={data.platform_total} duration={2.6} />
            </div>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
              Platform Orders Processed · All Time
            </div>
            <span className="mt-3 inline-block rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 font-mono text-[9px] tracking-[0.2em] text-amber-300" data-testid="admin-platform-simulated-badge">
              SIMULATED — ADMIN VISIBILITY ONLY
            </span>
          </div>
          {data.maintenance?.enabled && (
            <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-amber-300">
              MAINTENANCE MODE ACTIVE
            </span>
          )}
        </div>
      </motion.section>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.55 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6"
          data-testid="admin-chart-orders"
        >
          <h3 className="text-sm font-semibold text-neutral-100">Orders — last 14 days</h3>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.chart} margin={{ left: -20, right: 8, top: 4 }}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgb(var(--gold))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="rgb(var(--gold))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#6b6b74", fontSize: 10 }} tickLine={false} axisLine={false} interval={2} />
                <YAxis tick={{ fill: "#6b6b74", fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "rgb(var(--gold) / 0.3)" }} />
                <Area type="monotone" dataKey="orders" stroke="rgb(var(--gold))" strokeWidth={2} fill="url(#goldFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.62 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6"
          data-testid="admin-chart-revenue"
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-neutral-100">Revenue — last 14 days</h3>
            <button onClick={downloadRevenuePdf} disabled={revBusy} data-testid="revenue-pdf-button"
              className="flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-[10px] font-bold tracking-[0.12em] text-goldlight transition-colors hover:bg-gold hover:text-black disabled:opacity-50">
              {revBusy ? <Loader size={12} className="animate-spin" /> : <FileDown size={12} />}
              REVENUE STATEMENT (PDF)
            </button>
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chart} margin={{ left: -20, right: 8, top: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#6b6b74", fontSize: 10 }} tickLine={false} axisLine={false} interval={2} />
                <YAxis tick={{ fill: "#6b6b74", fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgb(var(--gold) / 0.06)" }} formatter={(v) => [inr(v), "Revenue"]} />
                <Bar dataKey="revenue" fill="rgb(var(--gold))" radius={[4, 4, 0, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
        className="mt-6 rounded-2xl border border-white/10 bg-panel/80 p-6"
        data-testid="admin-activity-card"
      >
        <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
          <ScrollText size={15} className="text-gold" /> Recent admin activity
        </h3>
        <div className="mt-4 space-y-1">
          {data.recent_activity.length === 0 && (
            <p className="py-4 text-sm text-neutral-500">No admin actions logged yet.</p>
          )}
          {data.recent_activity.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-white/[0.03]">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] text-gold/70">{a.admin}</span>
                <span className="text-neutral-300">{a.action}</span>
                <span className="font-mono text-xs text-neutral-500">{a.target}</span>
              </div>
              <span className="font-mono text-[10px] text-neutral-600">{fmtDate(a.created_at)}</span>
            </div>
          ))}
        </div>
      </motion.section>
    </div>
  );
}
